import type { CampaignEvent, EventType } from '../types/analytics-api.types';

export const SKIP_REASON_LABEL: Record<string, string> = {
  unsubscribed: 'Unsubscribed',
  pending: 'Pending',
  suppressed: 'Suppressed',
  noAddress: 'No address',
  invalidEmail: 'Invalid email',
  noSubscriptionStatus: 'No subscription status',
};

export const EVENT_TYPE_OPTIONS: Array<{ value: EventType | 'all'; label: string }> = [
  { value: 'all', label: 'All event types' },
  { value: 'queued', label: 'Queued' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'open', label: 'Opened' },
  { value: 'click', label: 'Clicked' },
  { value: 'bounce', label: 'Bounced' },
  { value: 'complaint', label: 'Marked as spam' },
  { value: 'unsubscribe', label: 'Unsubscribed' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

const ALERT_METRIC: Record<string, string> = {
  'complaint-rate-high': 'Spam complaints',
  'bounce-rate-high': 'Bounce rate',
  'unsubscribe-rate-high': 'Unsubscribe rate',
};

const ALERT_WHY: Record<string, string> = {
  'complaint-rate-high':
    'This is the number that gets a sending domain blocked. Above this limit mailbox providers begin throttling or rejecting outright.',
  'bounce-rate-high':
    'A high bounce rate signals stale list data and damages sender reputation for every later send.',
  'unsubscribe-rate-high':
    'Sustained opt-outs at this level usually mean targeting or frequency, not content.',
};

export function alertHeadline(
  code: string,
  thresholdLabel: string,
  campaignName: string | null
): string {
  const metric = ALERT_METRIC[code] || 'Rate';
  const where = campaignName ? ` on "${campaignName}"` : '';
  return `${metric} over ${thresholdLabel}${where}`;
}

export function alertWhy(code: string): string {
  return ALERT_WHY[code] || 'This metric is over its threshold.';
}

export function eventLabel(type: string, subType: string | null): string {
  if (type === 'bounce' && subType === 'hard') return 'Bounced (hard)';
  if (type === 'bounce' && subType === 'soft') return 'Bounced (soft)';
  if (type === 'bounce' && subType === 'block') return 'Bounced (blocked)';
  if (type === 'bounce') return 'Bounced';
  if (type === 'skipped') {
    const reason = subType ? SKIP_REASON_LABEL[subType] || subType : 'unknown reason';
    return `Skipped — ${reason}`;
  }
  if (type === 'complaint') return 'Marked as spam';
  if (type === 'open') return 'Opened';
  if (type === 'click') return 'Clicked';
  if (type === 'delivered') return 'Delivered';
  if (type === 'sent') return 'Sent';
  if (type === 'queued') return 'Queued';
  if (type === 'unsubscribe') return 'Unsubscribed';
  if (type === 'failed') return 'Failed';
  return type;
}

/** Name merge-tag destinations instead of linking them. */
export function namedLinkLabel(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.includes('{{unsubscribeUrl}}')) return 'Unsubscribe link';
  if (url.includes('{{preferencesUrl}}')) return 'Preferences link';
  if (url.includes('{{')) return 'Merge-tag link';
  return null;
}

export function eventTone(
  event: Pick<CampaignEvent, 'type'>
): 'neutral' | 'success' | 'info' | 'warning' | 'error' {
  switch (event.type) {
    case 'delivered':
      return 'success';
    case 'open':
    case 'click':
    case 'sent':
      return 'info';
    case 'bounce':
    case 'complaint':
    case 'failed':
      return 'error';
    case 'unsubscribe':
    case 'skipped':
      return 'warning';
    default:
      return 'neutral';
  }
}
